/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"
import { cn } from "cn"

// Figma-Variante "default outline" wird als Wert "outline" normalisiert
// (gängige Card-API-Konvention statt wörtlicher Übernahme).
const cardVariants = cva(
  "flex flex-col gap-4 rounded-xl bg-backgrounds-primary p-4",
  {
    variants: {
      variant: {
        default: "",
        outline: "border border-neutrals-v2-400",
        // Schatten ist deckungsgleich mit dem bestehenden --shadow-popover-Token.
        elevated: "shadow-popover",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type CardVariant = NonNullable<
  VariantProps<typeof cardVariants>["variant"]
>

export type CardProps = ComponentProps<"div"> &
  VariantProps<typeof cardVariants>

function Card({ className, variant = "default", ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-variant={variant}
      className={cn(cardVariants({ variant, className }))}
      {...props}
    />
  )
}

// Kein Figma-Design für Header/Title/Description/Action/Content/Footer im
// "Card"-Component-Set (nur ein generischer Content-Slot) — Struktur und
// Klassen folgen daher der shadcn-Referenz, Farben auf Projekt-Tokens gemappt.
function CardHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "grid auto-rows-min items-start gap-1 has-data-[slot=card-action]:grid-cols-[1fr_auto]",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-base leading-6 font-semibold text-labels-primary",
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm leading-5 text-labels-secondary", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn(className)} {...props} />
}

function CardFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center gap-2", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  cardVariants,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
}
